import React from 'react';
import { 
  Box, Paper, Button, Card, Typography, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Chip 
} from '@mui/material';
import { CheckCircle, Schedule, WarningAmber } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PortfolioInfoCards from './PortfolioInfoCards';
import SectorScoresCard from './SectorScoresCard';
import TransactionCostCard from './TransactionCostCard';
import CorrelationHeatmapCard from './CorrelationHeatmapCard';
import RebalancePlanCard from './RebalancePlanCard';
import BacktestResultsCard from './BacktestResultsCard';

// Signal configuration for stock actions
const SIGNAL_CONFIG = {
  BUY_NOW: { label: 'BUY NOW', color: '#4caf50' },
  WAIT_PULLBACK: { label: 'WAIT PULLBACK', color: '#ff9800' },
  HOLD: { label: 'HOLD', color: '#2196f3' },
  SELL: { label: 'SELL', color: '#f44336' }
};

/**
 * PortfolioTabs Component
 * 
 * Displays tabbed interface for portfolio data:
 * - BUY NOW: Stocks ready to buy immediately
 * - WATCHLIST: Stocks waiting for better entry
 * - OVERVIEW: Market analysis and portfolio details
 * 
 * @param {string} activeTab - Currently active tab
 * @param {function} setActiveTab - Function to change active tab
 * @param {Object} portfolio - Portfolio data
 * @param {Object} killSwitch - Kill-switch status
 * @param {Object} marketBreadth - Market breadth data
 * @param {Object} sectorRotation - Sector rotation analysis
 * @param {string} currentLang - Current language (vi/en)
 */
const PortfolioTabs = ({ 
  activeTab, 
  setActiveTab, 
  portfolio, 
  killSwitch, 
  marketBreadth, 
  sectorRotation, 
  currentLang,
  backtestResults,
  backtestLoading
}) => {
  const { t, i18n } = useTranslation();

  if (!portfolio) return null;

  // Calculate counts from actual data arrays
  const buyNowStocks = (portfolio.portfolio || []).filter(s => s.state === 'BUY_NOW');
  const watchlistStocks = (portfolio.watchlist || []).filter(s => s.state === 'WAIT');
  
  const tabs = [
    {
      id: 'buy-now',
      label: t('riskManagement.portfolioConstruction.tabs.buyNow'),
      count: portfolio.summary?.buy_now_count || buyNowStocks.length,
      color: '#4caf50'
    },
    {
      id: 'watchlist',
      label: t('riskManagement.portfolioConstruction.tabs.watchlist'),
      count: portfolio.summary?.wait_pullback_count || watchlistStocks.length,
      color: '#ff9800'
    },
    {
      id: 'overview',
      label: t('riskManagement.portfolioConstruction.tabs.overview'),
      count: null,
      color: 'primary.main'
    }
  ];

  return (
    <>
      {/* Tabs Navigation */}
      <Paper sx={{ mb: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {tabs.map((tab) => (
              <Button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                sx={{
                  px: 3,
                  py: 1.5,
                  borderRadius: 0,
                  borderBottom: activeTab === tab.id ? 3 : 0,
                  borderColor: tab.color,
                  color: activeTab === tab.id ? tab.color : 'text.secondary',
                  fontWeight: activeTab === tab.id ? 700 : 500,
                  '&:hover': {
                    bgcolor: tab.id === 'overview' ? 'primary.lighter' : `${tab.color}10`,
                  }
                }}
              >
                {tab.label} {tab.count !== null && `(${tab.count})`}
              </Button>
            ))}
          </Box>
        </Box>
      </Paper>

      {/* BUY NOW Tab Content */}
      {activeTab === 'buy-now' && buyNowStocks.length > 0 && (
        <BuyNowTable portfolio={portfolio} buyNowStocks={buyNowStocks} t={t} i18n={i18n} />
      )}

      {/* WATCHLIST Tab Content */}
      {activeTab === 'watchlist' && watchlistStocks.length > 0 && (
        <WatchlistTable portfolio={portfolio} watchlistStocks={watchlistStocks} t={t} i18n={i18n} />
      )}

      {/* OVERVIEW Tab Content */}
      {activeTab === 'overview' && portfolio && (
        <Card 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            border: '1px solid rgba(0,0,0,0.06)',
            overflow: 'hidden',
          }}
        >
          {/* Kill Switch - Simplified banner without cards */}
          {killSwitch && (
            <Box sx={{ px: 3, pt: 3, pb: 2 }}>
              <Box 
                sx={{ 
                  p: 2,
                  borderRadius: 2,
                  background: killSwitch.active 
                    ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                    : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  mb: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box 
                      sx={{ 
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: 'white',
                        boxShadow: '0 0 12px rgba(255,255,255,0.6)',
                      }}
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'white', fontSize: '0.9rem' }}>
                      {killSwitch.title?.[currentLang] || 'Hệ Thống Báo Về'}: {killSwitch.status?.[currentLang] || 'N/A'}
                    </Typography>
                  </Box>
                  <Chip 
                    label={killSwitch.risk_level_i18n?.[currentLang] || 'N/A'}
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.25)',
                      color: 'white',
                      fontWeight: 700,
                      height: 22,
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
              </Box>

              {/* Defensive Alert - Inline */}
              {killSwitch.active && killSwitch.recommended_exposure !== null && (
                <Box 
                  sx={{ 
                    p: 1.5,
                    bgcolor: 'rgba(239, 68, 68, 0.1)',
                    borderRadius: 1.5,
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <WarningAmber sx={{ fontSize: 18, color: '#dc2626' }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.75rem' }}>
                      {t('riskManagement.portfolioConstruction.overview.killSwitch.defensiveModeActivated')}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#991b1b', fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                    {t('riskManagement.portfolioConstruction.overview.killSwitch.recommendedMaxExposure')}: <strong>{(killSwitch.recommended_exposure * 100).toFixed(0)}%</strong>
                  </Typography>
                </Box>
              )}

              {/* Trigger Details - Simple inline grid NO individual cards */}
              {killSwitch.details && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 2 }}>
                  {Object.entries(killSwitch.details).map(([key, detail]) => {
                    const isTriggered = detail.triggered;
                    return (
                      <Box 
                        key={key}
                        sx={{ 
                          p: 1.25,
                          borderRadius: 1.5,
                          border: '1px solid',
                          borderColor: isTriggered ? 'rgba(239, 68, 68, 0.3)' : 'rgba(16, 185, 129, 0.3)',
                          bgcolor: isTriggered ? 'rgba(239, 68, 68, 0.08)' : 'rgba(16, 185, 129, 0.08)',
                        }}
                      >
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            display: 'block',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            fontSize: '0.65rem',
                            color: 'text.secondary',
                            mb: 0.5,
                          }}
                        >
                          {key.replace('_', ' ')}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 700,
                            color: isTriggered ? '#dc2626' : '#059669',
                            fontSize: '0.8rem',
                            mb: 0.5,
                          }}
                        >
                          {isTriggered 
                            ? t('riskManagement.portfolioConstruction.overview.killSwitch.triggered')
                            : t('riskManagement.portfolioConstruction.overview.killSwitch.ok')}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            display: 'block',
                            fontSize: '0.7rem',
                            color: 'text.secondary',
                            lineHeight: 1.3,
                          }}
                        >
                          {detail.message_i18n?.[currentLang] || ''}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Active Warnings - Inline chips */}
              {killSwitch.warnings && killSwitch.warnings.length > 0 && (
                <Box 
                  sx={{ 
                    p: 1.5,
                    bgcolor: 'rgba(245, 158, 11, 0.1)',
                    borderRadius: 1.5,
                    border: '1px solid rgba(245, 158, 11, 0.2)',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#d97706', fontSize: '0.7rem', display: 'block', mb: 1 }}>
                    {t('riskManagement.portfolioConstruction.overview.killSwitch.activeWarnings')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {killSwitch.warnings.map((warning, idx) => (
                      <Typography key={idx} variant="caption" sx={{ fontSize: '0.7rem', color: '#92400e' }}>
                        • {warning}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Portfolio Info - Compact Sections */}
          <Box sx={{ p: 3 }}>
            <PortfolioInfoCards portfolio={portfolio} />
          </Box>

          {/* Conditional Cards - Only if data exists */}
          {backtestResults && (
            <Box sx={{ px: 3, pb: 3 }}>
              <BacktestResultsCard 
                backtestResults={backtestResults}
                loading={backtestLoading}
              />
            </Box>
          )}

          {portfolio.transaction_costs && (
            <Box sx={{ px: 3, pb: 3 }}>
              <TransactionCostCard 
                transactionCosts={portfolio.transaction_costs}
                exposure={portfolio.exposure}
                netExposure={portfolio.net_exposure}
              />
            </Box>
          )}

          {portfolio.correlation_metrics && (
            <Box sx={{ px: 3, pb: 3 }}>
              <CorrelationHeatmapCard 
                correlationMetrics={portfolio.correlation_metrics}
              />
            </Box>
          )}

          {portfolio.rebalance_plan && (
            <Box sx={{ px: 3, pb: 3 }}>
              <RebalancePlanCard 
                rebalancePlan={portfolio.rebalance_plan}
              />
            </Box>
          )}

          {/* Market Analysis Section */}
          <Box sx={{ bgcolor: 'rgba(0,0,0,0.02)', px: 3, py: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5, fontSize: '0.95rem', color: 'text.primary' }}>
              {t('riskManagement.portfolioConstruction.overview.marketAnalysis')}
            </Typography>

            {/* All metrics in compact grid - NO individual cards */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Market Health - Inline metrics */}
              {marketBreadth && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'text.primary', mb: 1.5 }}>
                    {t('riskManagement.portfolioConstruction.overview.marketHealthRisk')}
                  </Typography>
                  
                  {/* Compact table-like layout */}
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(3, 1fr)', 
                    gap: 0.5,
                    bgcolor: 'white',
                    borderRadius: 1.5,
                    border: '1px solid rgba(0,0,0,0.08)',
                    overflow: 'hidden'
                  }}>
                    {/* Row 1 */}
                    <Box sx={{ p: 1.5, borderRight: '1px solid rgba(0,0,0,0.05)' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                        {t('riskManagement.portfolioConstruction.overview.marketRiskScore')}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#f44336', fontSize: '1.25rem' }}>
                        {marketBreadth.market_risk_score?.toFixed(1) || 'N/A'}
                        <Typography component="span" sx={{ fontSize: '0.65rem', color: 'text.secondary', ml: 0.3 }}>/100</Typography>
                      </Typography>
                    </Box>
                    
                    <Box sx={{ p: 1.5, borderRight: '1px solid rgba(0,0,0,0.05)' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                        {t('riskManagement.portfolioConstruction.overview.targetExposure')}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main', fontSize: '1.25rem' }}>
                        {((marketBreadth.target_exposure || 0) * 100).toFixed(0)}%
                      </Typography>
                    </Box>
                    
                    <Box sx={{ p: 1.5 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                        {t('riskManagement.portfolioConstruction.overview.aboveMA50')}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#4caf50', fontSize: '1.25rem' }}>
                        {marketBreadth.breadth?.pct_above_ma50?.toFixed(1) || '0'}%
                      </Typography>
                    </Box>
                    
                    {/* Row 2 */}
                    <Box sx={{ p: 1.5, borderTop: '1px solid rgba(0,0,0,0.05)', borderRight: '1px solid rgba(0,0,0,0.05)' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                        {t('riskManagement.portfolioConstruction.overview.breakout20')}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#2196f3', fontSize: '1.25rem' }}>
                        {marketBreadth.breadth?.pct_breakout20?.toFixed(1) || '0'}%
                      </Typography>
                    </Box>
                    
                    <Box sx={{ p: 1.5, borderTop: '1px solid rgba(0,0,0,0.05)', borderRight: '1px solid rgba(0,0,0,0.05)' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                        {t('riskManagement.portfolioConstruction.overview.advDecRatio')}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#ff9800', fontSize: '1.25rem' }}>
                        {marketBreadth.breadth?.adv_dec_ratio?.toFixed(2) || '0'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ p: 1.5, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                        {t('riskManagement.portfolioConstruction.overview.newHighLow52Week')}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 800, 
                            color: marketBreadth.breadth?.new_high_low > 0 ? '#4caf50' : '#f44336', 
                            fontSize: '1.25rem' 
                          }}
                        >
                          {marketBreadth.breadth?.new_high_low > 0 ? '+' : ''}{marketBreadth.breadth?.new_high_low || 0}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                          {marketBreadth.breadth?.new_high_low > 0 
                            ? t('riskManagement.portfolioConstruction.overview.positive')
                            : marketBreadth.breadth?.new_high_low < 0 
                            ? t('riskManagement.portfolioConstruction.overview.negative')
                            : t('riskManagement.portfolioConstruction.overview.neutral')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  {/* Date - separate line */}
                  <Box sx={{ mt: 1, px: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                      {t('riskManagement.portfolioConstruction.overview.dateLabel')}: <Typography component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{marketBreadth.date || 'N/A'}</Typography>
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Sector Rotation - Compact inline */}
              {sectorRotation && sectorRotation.ranking && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'text.primary' }}>
                      {t('riskManagement.portfolioConstruction.overview.sectorRotation')}
                    </Typography>
                    <Chip 
                      label={t('riskManagement.portfolioConstruction.overview.sectorsCountLabel', { count: sectorRotation.ranking.length })}
                      size="small"
                      sx={{ 
                        bgcolor: 'rgba(102, 126, 234, 0.1)',
                        color: '#667eea',
                        fontWeight: 600,
                        height: 22,
                        fontSize: '0.7rem',
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
                    {sectorRotation.ranking.map((sector) => {
                      const signal = sectorRotation.rotation_signals[sector.sector] || 'NEUTRAL';
                      const getColor = (sig) => {
                        if (sig === 'LEADING') return '#4caf50';
                        if (sig === 'LAGGING') return '#f44336';
                        return '#ff9800';
                      };
                      const color = getColor(signal);
                      const sectorName = sector.sector_name 
                        ? sector.sector_name[currentLang] 
                        : sector.sector.replace(/_/g, ' ');
                      
                      // Helper to safely format percentage
                      const formatPercent = (value) => {
                        if (value === null || value === undefined || isNaN(value)) return 'N/A';
                        // Check if already in percentage format (> 1 or < -1)
                        const percent = Math.abs(value) > 1 ? value : value * 100;
                        // Cap at reasonable range
                        const capped = Math.max(-100, Math.min(100, percent));
                        return `${capped > 0 ? '+' : ''}${capped.toFixed(1)}%`;
                      };
                      
                      // Get color for value
                      const getValueColor = (value) => {
                        if (value === null || value === undefined || isNaN(value)) return 'text.secondary';
                        const val = Math.abs(value) > 1 ? value : value * 100;
                        return val > 0 ? '#4caf50' : val < 0 ? '#f44336' : 'text.secondary';
                      };

                      return (
                        <Box 
                          key={sector.sector}
                          sx={{ 
                            p: 1.5,
                            bgcolor: 'white',
                            borderRadius: 1.5,
                            border: '1px solid rgba(0,0,0,0.08)',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: color,
                              boxShadow: `0 4px 12px ${color}25`,
                              transform: 'translateY(-2px)',
                            }
                          }}
                        >
                          {/* Header: Name + Score */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.primary' }}>
                              {sectorName}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800, color, fontSize: '1.25rem', lineHeight: 1 }}>
                              {sector.score.toFixed(0)}
                            </Typography>
                          </Box>
                          
                          {/* Signal Badge */}
                          <Box sx={{ mb: 1 }}>
                            <Chip 
                              label={signal}
                              size="small"
                              sx={{ 
                                height: 18,
                                fontSize: '0.6rem',
                                fontWeight: 700,
                                bgcolor: `${color}15`,
                                color: color,
                                '& .MuiChip-label': { px: 1 }
                              }}
                            />
                          </Box>
                          
                          {/* Details Grid - 3 columns fixed */}
                          <Box sx={{ 
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr',
                            gap: 1,
                            pt: 1,
                            borderTop: '1px solid rgba(0,0,0,0.05)'
                          }}>
                            {/* Momentum */}
                            <Box>
                              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', display: 'block' }}>
                                {t('riskManagement.portfolioConstruction.overview.momentum')}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.75rem', color: getValueColor(sector.momentum) }}>
                                {formatPercent(sector.momentum)}
                              </Typography>
                            </Box>
                            
                            {/* Breadth */}
                            <Box>
                              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', display: 'block' }}>
                                {t('riskManagement.portfolioConstruction.overview.breadth')}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.75rem', color: sector.breadth > 50 ? '#4caf50' : '#f44336' }}>
                                {sector.breadth ? `${sector.breadth.toFixed(0)}%` : 'N/A'}
                              </Typography>
                            </Box>
                            
                            {/* Relative Strength */}
                            <Box>
                              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', display: 'block' }}>
                                {t('riskManagement.portfolioConstruction.overview.rsIndex')}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'primary.main' }}>
                                {sector.relative_strength ? sector.relative_strength.toFixed(1) : 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                          
                          {/* Rank - separate row if exists */}
                          {sector.rank > 0 && (
                            <Box sx={{ pt: 0.75 }}>
                              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', display: 'inline', mr: 0.5 }}>
                                {t('riskManagement.portfolioConstruction.overview.rank')}:
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color: 'primary.main' }}>
                                #{sector.rank}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Sector Scores - Already compact */}
              {portfolio.sectors && (
                <SectorScoresCard sectors={portfolio.sectors} />
              )}
            </Box>
          </Box>
        </Card>
      )}
    </>
  );
};

/**
 * BuyNowTable - Displays stocks ready to buy
 */
const BuyNowTable = ({ portfolio, buyNowStocks, t, i18n }) => {
  const buyNowCount = buyNowStocks.length;
  const allocatedNow = portfolio.summary?.allocated_now || 
    (buyNowStocks.reduce((sum, s) => sum + (s.weight || 0), 0));
  
  return (
    <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '2px solid #4caf50' }}>
      <Box sx={{ p: 2, bgcolor: '#4caf5015', borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
        <CheckCircle sx={{ fontSize: '1.5rem', mr: 1, color: '#4caf50' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('riskManagement.portfolioConstruction.buyNowCard.title', { count: buyNowCount })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('riskManagement.portfolioConstruction.buyNowCard.allocated', { percent: (allocatedNow * 100).toFixed(1) })}
          </Typography>
        </Box>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>{t('riskManagement.portfolioConstruction.table.ticker')}</TableCell>
              <TableCell align="right">{t('riskManagement.portfolioConstruction.table.weight')}</TableCell>
              <TableCell align="right">{t('riskManagement.portfolioConstruction.table.entryScore')}</TableCell>
              <TableCell>{t('riskManagement.portfolioConstruction.table.signal')}</TableCell>
              <TableCell>{t('riskManagement.portfolioConstruction.table.action')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {buyNowStocks.map((stock, index) => {
              const signalConfig = SIGNAL_CONFIG[stock.MultiTFSignal || stock.state || 'BUY_NOW'] || SIGNAL_CONFIG.BUY_NOW;
              const entryScore = stock.EntrySafety || 0;
              const sectorName = stock.sector?.en || stock.sector?.vi || stock.sector || 'N/A';
              const sectorVi = stock.sector?.vi || stock.sector?.en || stock.sector || 'N/A';
              
              return (
                <TableRow key={stock.ticker || index} hover sx={{ bgcolor: '#4caf5008' }}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 0.3 }}>
                        {stock.ticker}
                      </Typography>
                      <Typography variant="caption" sx={{ 
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        fontSize: '0.65rem',
                        color: 'text.secondary'
                      }}>
                        {i18n.language === 'vi' ? sectorVi : sectorName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                      {((stock.weight || 0) * 100).toFixed(2)}%
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 700,
                        color: entryScore >= 70 ? 'success.main' : entryScore >= 40 ? 'warning.main' : 'error.main'
                      }}
                    >
                      {entryScore.toFixed(0)}/100
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={signalConfig.label}
                      size="small"
                      sx={{ 
                        bgcolor: `${signalConfig.color}20`,
                        color: signalConfig.color,
                        fontWeight: 600
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                      {t('riskManagement.portfolioConstruction.table.buyNowAtMarket')}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

/**
 * WatchlistTable - Displays stocks waiting for better entry
 */
const WatchlistTable = ({ portfolio, watchlistStocks, t, i18n }) => {
  const watchlistCount = watchlistStocks.length;
  const cashReserved = portfolio.summary?.cash_reserved || 0;
  
  return (
    <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '2px solid #ff9800' }}>
      <Box sx={{ p: 2, bgcolor: '#ff980015', borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
        <Schedule sx={{ fontSize: '1.5rem', mr: 1, color: '#ff9800' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('riskManagement.portfolioConstruction.watchlistCard.title', { count: watchlistCount })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('riskManagement.portfolioConstruction.watchlistCard.reserved', { percent: (cashReserved * 100).toFixed(0) })}
          </Typography>
        </Box>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>{t('riskManagement.portfolioConstruction.table.ticker')}</TableCell>
              <TableCell align="right">{t('riskManagement.portfolioConstruction.table.currentPrice')}</TableCell>
              <TableCell align="right">{t('riskManagement.portfolioConstruction.table.buyZone')}</TableCell>
              <TableCell align="right">{t('riskManagement.portfolioConstruction.table.distance')}</TableCell>
              <TableCell align="right">{t('riskManagement.portfolioConstruction.table.entryScore')}</TableCell>
              <TableCell>{t('riskManagement.portfolioConstruction.table.reason')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {watchlistStocks.map((stock, index) => {
              // Get buy zone from orders_plan if available
              const orderPlan = (portfolio.orders_plan || []).find(o => o.ticker === stock.ticker);
              const currentPrice = stock.close || 0;
              const buyZoneLow = orderPlan?.buy_zone_low || 0;
              const buyZoneHigh = orderPlan?.buy_zone_high || 0;
              const distance = buyZoneHigh > 0 ? ((currentPrice - buyZoneHigh) / buyZoneHigh * 100) : 0;
              const entryScore = stock.EntrySafety || 0;
              const sectorName = stock.sector?.en || stock.sector?.vi || stock.sector || 'N/A';
              const sectorVi = stock.sector?.vi || stock.sector?.en || stock.sector || 'N/A';
              
              return (
                <TableRow key={stock.ticker || index} hover sx={{ bgcolor: '#ff980008' }}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                        <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                          {stock.ticker}
                        </Typography>
                        {stock.cycle_position > 0.85 && (
                          <Chip 
                            label={t('riskManagement.portfolioConstruction.table.overextended')} 
                            size="small" 
                            color="error"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.6rem' }}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ 
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        fontSize: '0.65rem',
                        color: 'text.secondary'
                      }}>
                        {i18n.language === 'vi' ? sectorVi : sectorName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {currentPrice.toLocaleString('vi-VN')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {t('riskManagement.portfolioConstruction.table.rsi')}: {stock.rsi14?.toFixed(1) || 'N/A'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {buyZoneLow > 0 ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                          {buyZoneLow.toLocaleString('vi-VN', { maximumFractionDigits: 0 })} - {buyZoneHigh.toLocaleString('vi-VN', { maximumFractionDigits: 0 })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('riskManagement.portfolioConstruction.table.ma20')}: {stock.ma20 ? stock.ma20.toLocaleString('vi-VN', { maximumFractionDigits: 0 }) : 'N/A'}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {t('riskManagement.portfolioConstruction.table.calculating')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {distance > 0 ? (
                      <Chip
                        label={`↑ ${distance.toFixed(1)}%`}
                        size="small"
                        sx={{ 
                          bgcolor: distance > 10 ? '#f4433620' : '#ff980020',
                          color: distance > 10 ? '#f44336' : '#ff9800',
                          fontWeight: 600
                        }}
                      />
                    ) : distance < -2 ? (
                      <Chip
                        label={t('riskManagement.portfolioConstruction.table.inZone')}
                        size="small"
                        color="success"
                        sx={{ fontWeight: 600 }}
                      />
                    ) : (
                      <Chip
                        label={`→ ${Math.abs(distance).toFixed(1)}%`}
                        size="small"
                        color="warning"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 700,
                        color: entryScore >= 50 ? 'success.main' : entryScore >= 20 ? 'warning.main' : 'error.main'
                      }}
                    >
                      {entryScore.toFixed(0)}/100
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 250, display: 'block' }}>
                      {(() => {
                        // Try to get reason from multiple sources
                        const reason = orderPlan?.notes?.[0] || 
                                      orderPlan?.reason || 
                                      stock.wait_reason ||
                                      stock.notes ||
                                      '';
                        
                        // If reason is empty or corrupted (single character like 'đ')
                        if (!reason || reason.length <= 2) {
                          // Generate default reason based on data
                          if (stock.cycle_position > 0.85) {
                            return t('riskManagement.portfolioConstruction.waitReasons.highCyclePosition');
                          }
                          if (stock.rsi14 > 70) {
                            return t('riskManagement.portfolioConstruction.waitReasons.highRSI');
                          }
                          if (distance > 5) {
                            return t('riskManagement.portfolioConstruction.waitReasons.aboveBuyZone', { distance: distance.toFixed(1) });
                          }
                          return t('riskManagement.portfolioConstruction.waitReasons.waitBetterEntry');
                        }
                        
                        return reason;
                      })()}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export default PortfolioTabs;
